import { Stack } from '@mui/material';

import { ImpactCo2AnswerStatsPublic } from '@/domain/impact-stats/entities/stats-public.entity';
import { computeStatsForThisAccommodationType } from '@/domain/impact-stats/utils/compute-stats-for-specific-case';
import { translate } from '@/ui/i18n';
import { ExtrapolationInput } from '@/ui/pages/app/components/quiz-panel/co2/components/public-answers/ExtrapolationInput';

import { BoxData } from './BoxData';
import { useExtrapolationAccommodation } from './hooks/useExtrapolationAccommodation';
import { TextData } from './TextData';

export function AccommodationData({
  statsPublic,
}: {
  statsPublic: ImpactCo2AnswerStatsPublic;
}) {
  const {
    extrapolationValue,
    setExtrapolationValue,
    statsPublicOverride,
    valueColor,
    isHighFactor,
  } = useExtrapolationAccommodation(statsPublic);

  return (
    <Stack sx={{ marginY: 2 }}>
      <Stack sx={{ flexDirection: 'row', justifyContent: 'flex-end', mb: 2 }}>
        <ExtrapolationInput
          value={extrapolationValue}
          onChange={setExtrapolationValue}
          personCount={statsPublic.publicAccommodationSumTotalGuestCount}
          showWarning={isHighFactor}
        />
      </Stack>
      <AccommodationTitle
        statsPublic={statsPublicOverride}
        valueColor={valueColor}
      />
      {statsPublicOverride.publicAccommodation.map((x) => (
        <AccommodationItem
          key={x.accommodationType}
          item={x}
          totalGuestCount={
            statsPublicOverride.publicAccommodationSumTotalGuestCount
          }
          valueColor={valueColor}
        />
      ))}
    </Stack>
  );
}

function AccommodationTitle({
  statsPublic,
  valueColor,
}: {
  statsPublic: ImpactCo2AnswerStatsPublic;
  valueColor?: string;
}) {
  const totalAccommodationScore = statsPublic.publicAccommodation.reduce(
    (sum, x) => sum + x.totalScore,
    0,
  );
  return (
    <TextData
      label={translate('Quiz.number-of-days-spent-in-place')}
      value={translate('Quiz.night_cumulative', {
        count: statsPublic.numberOfNights,
      })}
      scoreCo2={totalAccommodationScore}
      valueColor={valueColor}
    />
  );
}

function AccommodationItem({
  item,
  totalGuestCount,
  valueColor,
}: {
  item: ImpactCo2AnswerStatsPublic['publicAccommodation'][number];
  totalGuestCount: number;
  valueColor?: string;
}) {
  const stats = computeStatsForThisAccommodationType(item, totalGuestCount);
  return (
    <BoxData
      title={translate(
        `ImpactQuestion.ACCOMMODATION.accommodationTypeValues.${item.accommodationType}`,
      )}
      score={item.totalScore}
      lines={[
        translate('Projects.X-persons', { count: item.totalGuestCount }),
        translate('Quiz.night_cumulative', {
          count: item.totalNumberOfNights,
        }),
      ]}
      footer={translate('Quiz.public-accommodation-stats-sentence', {
        percentageGuest: (stats.percentageGuest * 100).toFixed(1),
        averageNumberOfNights: stats.averageNumberOfNights.toFixed(0),
      })}
      valueColor={valueColor}
    />
  );
}
